/**
 * Server configurator (sections/si-server-config.liquid).
 *
 * Reads the server and the fitting in-stock parts from the section's JSON, draws the
 * memory, drive, caddy and rail groups, keeps every quantity inside stock, memory slots
 * and drive bays, and either adds the build to the cart (parts nested under the server
 * line through `parent_id`) or writes it into the quote form on the page. A stock server
 * ordered by quote (`quoteOnly`, snippets/si-quote-only-check) gets no cart button.
 *
 * Cart updates go through the same standard event the theme's own add-to-cart uses
 * (CartLinesUpdateEvent from @shopify/events), so the cart drawer and the cart count
 * refresh themselves. Without that module the build still goes in and the page moves
 * to the cart.
 */
import { formatMoney } from '@theme/money-formatting';

/** Memory slots per CPU socket by model. Unknown models fall back to 12. */
const SLOTS_PER_CPU = {
  R620: 12, R720: 12, R720XD: 12, R630: 12, R730: 12, R730XD: 12, R430: 6, R530: 6,
  T430: 6, T630: 12, R640: 12, R740: 12, R740XD: 12, R740XD2: 12, R840: 12, R940: 12,
  R440: 8, R540: 8, T440: 8, T640: 12, R6415: 16, R7415: 16, R7425: 16, R650: 16,
  R750: 16, R650XS: 8, R750XS: 8, R550: 8, R450: 8, R6525: 16, R7525: 16, R660: 16,
  R760: 16, DL360: 12, DL380: 12, DL560: 12, ML350: 12,
  R240: 4, R340: 4, T140: 4, T340: 4, R250: 4, R350: 4, T150: 4, T350: 4,
};

/** One-socket models: the title names no CPU count ("Xeon E-2314"), so the two-socket default would double the slots. */
const SINGLE_SOCKET = new Set(['R240', 'R340', 'T140', 'T340', 'R250', 'R350', 'T150', 'T350']);

const ICON_MINUS = '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6h8"/></svg>';
const ICON_PLUS = '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6h8M6 2v8"/></svg>';

/** @param {string} s */
const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/**
 * Liquid's `t` filter HTML-escapes translations (2,5&quot;, Caddy&#39;d). Decode them once so
 * esc() does not escape them a second time. DOMParser never runs scripts.
 * @param {string} s
 */
const decode = (s) => new DOMParser().parseFromString(`<!doctype html><body>${s ?? ''}`, 'text/html').body.textContent || '';

/** Group order on screen and in the build text. */
const GROUP_ORDER = { ram: 0, drive: 1, nvme: 2, caddy: 3, rails: 4 };

/** Bays an NVMe kit (one PCIe extender card plus its A0/B0 cable) opens on a 24-bay R740xd. */
const KIT_NVME_BAYS = 4;

/**
 * Fills {{ name }} placeholders of a translated string.
 * @param {string} s
 * @param {Record<string, string | number>} vars
 */
const fill = (s, vars) => String(s ?? '').replace(/{{\s*(\w+)\s*}}/g, (_, k) => String(vars[k] ?? ''));

/**
 * Reads bays, CPU count, memory slots and the memory already fitted from the server title
 * and spec text, the way the shop writes them ("Dell R740XD | 24SFF | 2xGold 6138 | ...",
 * "Ram- 16GB (1x 16GB 2666MT RDIMM)").
 * @param {{ title: string, spec: string }} server
 */
export function readServer(server) {
  const title = server.title || '';
  const upper = title.toUpperCase();
  const num = (re) => {
    const m = upper.match(re);
    return m ? parseInt(m[1], 10) : 0;
  };

  const diskless = /DISKLESS/.test(upper);
  const bays = {
    SFF: diskless ? 0 : num(/(\d+)\s*X?\s*SFF/),
    LFF: diskless ? 0 : num(/(\d+)\s*X?\s*LFF/),
    NVME: diskless ? 0 : num(/(\d+)\s*X?\s*(?:U\.2\s*)?NVME/),
  };

  const modelMatch = upper.match(/\b((?:R|T)\d{3,4}(?:XD2|XD|XS|XA)?|DL\d{3}|ML\d{3})\b/);
  const model = modelMatch ? modelMatch[1] : '';

  const cpuMatch = upper.match(/(\d)\s*X\s*(?:INTEL\s*)?(?:XEON\s*)?(?:GOLD|SILVER|BRONZE|PLATINUM|E5|E7|EPYC|\d{4})/);
  const cpus = SINGLE_SOCKET.has(model) ? 1 : cpuMatch ? Math.max(1, parseInt(cpuMatch[1], 10)) : 2;
  const slots = (SLOTS_PER_CPU[model] || 12) * cpus;

  // The memory line of the spec, up to the next separator.
  const spec = server.spec || '';
  const ramLine = (spec.match(/RAM\s*[-:]\s*([^|]*)/i) || [])[1] || '';
  const ramGb = (ramLine.match(/^\s*(\d+)\s*GB/i) || [])[1];
  const installedCount = ramGb === '0' ? 0 : parseInt((ramLine.match(/\(\s*(\d+)\s*x/i) || [])[1] || (ramGb ? '1' : '0'), 10);
  const installedKind = /LRDIMM/i.test(ramLine) ? 'LRDIMM' : /RDIMM/i.test(ramLine) ? 'RDIMM' : /UDIMM/i.test(ramLine) ? 'UDIMM' : '';

  return { bays, cpus, model, slots, noRam: ramGb === '0', installedCount, installedKind };
}

class SiServerConfig extends HTMLElement {
  /** @type {any} */ data;
  /** @type {ReturnType<typeof readServer>} */ hw;
  /** @type {Map<number, number>} variant id -> quantity */ qty = new Map();
  /** @type {Record<string, number>} caddy variant that follows the drives, per form factor */ autoCaddy = {};
  busy = false;
  /** NVMe kit offered on this server, and switched on */
  kitOffered = false;
  nvmeOn = false;
  baseNvme = 0;

  connectedCallback() {
    const source = this.querySelector('script[data-si-config]');
    if (!source || this.data) return;
    try {
      this.data = JSON.parse(source.textContent || '{}');
    } catch (error) {
      console.error('[si-server-config] bad data', error);
      return;
    }
    for (const key of Object.keys(this.data.t || {})) this.data.t[key] = decode(this.data.t[key]);
    this.data.assembly = decode(this.data.assembly);
    this.hw = readServer(this.data.server);
    this.baseNvme = this.hw.bays.NVME;
    const kit = (this.data.parts || []).filter((p) => p.g === 'nvme');
    // Offered on 24-bay SFF servers without NVMe of their own, when every kit part is in stock.
    this.kitOffered = kit.length > 0 && this.hw.bays.SFF >= 24 && this.baseNvme === 0 && kit.every((p) => p.cap >= 1);
    // Group order on screen; inside the NVMe kit the card (dearest part) leads, the cable follows.
    this.parts = this.#visibleParts().sort((a, b) => (GROUP_ORDER[a.g] ?? 9) - (GROUP_ORDER[b.g] ?? 9) || (a.g === 'nvme' ? b.price - a.price : 0));
    if (!this.parts.length) {
      this.closest('.si-config')?.setAttribute('hidden', '');
      return;
    }
    this.#render();
    this.#update();
  }

  /** Parts that can physically go into this server. */
  #visibleParts() {
    const { bays } = this.hw;
    return (this.data.parts || []).filter((part) => {
      if (part.g === 'nvme') return this.kitOffered;
      if (part.g === 'drive') {
        if (part.io === 'NVME') return bays.NVME > 0 || this.kitOffered;
        return bays[part.ff] > 0;
      }
      if (part.g === 'caddy') return bays[part.ff] > 0;
      return true;
    });
  }

  /** @param {number} cents */
  money(cents) {
    const { format, currency } = this.data.money || {};
    try {
      return formatMoney(cents, format || '{{amount}}', currency || 'EUR');
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  #render() {
    const t = this.data.t;
    const { bays, slots } = this.hw;
    const groups = { ram: [], drive: [], nvme: [], caddy: [], rails: [] };
    for (const part of this.parts) groups[part.g]?.push(part);

    // The first caddy of each form factor follows the drives.
    for (const part of groups.caddy) if (!(part.ff in this.autoCaddy)) this.autoCaddy[part.ff] = part.v;

    const bayMeta = [
      bays.SFF ? fill(t.bays_sff, { count: bays.SFF }) : '',
      bays.LFF ? fill(t.bays_lff, { count: bays.LFF }) : '',
      bays.NVME ? fill(t.bays_nvme, { count: bays.NVME }) : '',
    ]
      .filter(Boolean)
      .join(' · ');

    const fillLinks = Object.keys(this.autoCaddy)
      .map((ff) => `<button type="button" class="si-config__fill" data-fill="${ff}">${esc(fill(t.caddy_fill, { count: bays[ff] }))}</button>`)
      .join('');

    const html = [];
    html.push('<div class="si-config__grid"><div class="si-config__groups">');
    if (groups.ram.length) {
      html.push(this.#group('ram', t.ram, fill(t.slots, { count: slots }), groups.ram, '<p class="si-config__note" data-note="ram" hidden></p>'));
    }
    if (groups.drive.length) html.push(this.#group('drive', t.drives, bayMeta, groups.drive));
    if (groups.nvme.length) html.push(this.#nvmeGroup(groups.nvme));
    if (groups.caddy.length) {
      html.push(this.#group('caddy', t.caddies, '', groups.caddy, `<p class="si-config__meta si-config__caddy-auto">${esc(t.caddy_auto)}</p>`, fillLinks));
    }
    if (groups.rails.length) html.push(this.#railsGroup(t.rails, groups.rails));
    html.push('</div>');

    html.push(`<aside class="si-config__summary">
      <h3 class="si-config__summary-title">${esc(t.summary)}</h3>
      <ul class="si-config__lines" role="list" data-lines></ul>
      <div class="si-config__total">
        <span class="si-config__total-label">${esc(t.total)}</span>
        <strong class="si-config__total-sum" data-total aria-live="polite"></strong>
      </div>
      <p class="si-config__tax">${esc(t.tax)}</p>
      ${this.data.assembly ? `<p class="si-config__assembly">${esc(this.data.assembly)}</p>` : ''}
      <div class="si-config__actions">
        ${this.data.quoteOnly ? '' : `<button type="button" class="button si-config__add" data-add>${esc(t.add)}</button>`}
        <button type="button" class="${this.data.quoteOnly ? 'button' : 'button-secondary'} si-config__quote" data-quote>${esc(t.quote)}</button>
      </div>
      <p class="si-config__status" role="status" data-status></p>
    </aside></div>`);

    // The JSON stays in the element; only the drawn part is replaced.
    const source = this.querySelector('script[data-si-config]');
    this.innerHTML = '';
    if (source) this.append(source);
    this.insertAdjacentHTML('beforeend', html.join(''));

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
  }

  /**
   * @param {string} key
   * @param {string} title
   * @param {string} meta
   * @param {any[]} parts
   * @param {string} [extra]
   * @param {string} [headExtra]
   */
  #group(key, title, meta, parts, extra = '', headExtra = '') {
    const t = this.data.t;
    const rows = parts
      .map(
        (part) => `<li class="si-config__item" data-v="${part.v}">
          <a class="si-config__thumb" href="${esc(part.u)}" tabindex="-1" aria-hidden="true">${part.img ? `<img src="${esc(part.img)}" alt="" loading="lazy" width="56" height="56">` : ''}</a>
          <div class="si-config__text">
            <a class="si-config__title" href="${esc(part.u)}">${esc(part.t)}</a>
            <span class="si-config__stock si-config__stock--in">${esc(this.stockText(part))}</span>
          </div>
          <span class="si-config__price">${esc(this.money(part.price))}</span>
          <div class="si-config__qty" role="group" aria-label="${esc(part.t)}">
            <button type="button" class="si-config__step" data-step="-1" data-v="${part.v}" aria-label="${esc(fill(t.decrease, { title: part.t }))}">${ICON_MINUS}</button>
            <output class="si-config__count" data-count="${part.v}">0</output>
            <button type="button" class="si-config__step" data-step="1" data-v="${part.v}" aria-label="${esc(fill(t.increase, { title: part.t }))}">${ICON_PLUS}</button>
          </div>
        </li>`
      )
      .join('');
    return `<section class="si-config__group" data-group="${key}">
      <header class="si-config__group-head">
        <h3 class="si-config__group-title">${esc(title)}</h3>
        ${meta ? `<span class="si-config__meta" data-meta="${key}">${esc(meta)}</span>` : ''}
        ${headExtra}
      </header>
      ${extra}
      <ul class="si-config__list" role="list">${rows}</ul>
    </section>`;
  }

  /**
   * The NVMe kit as one switch: the extender card and its cable are added together.
   * @param {any[]} parts
   */
  #nvmeGroup(parts) {
    const t = this.data.t;
    const lead = parts[0];
    const price = parts.reduce((n, p) => n + p.price, 0);
    const names = parts.map((p) => p.t).join(', ');
    return `<section class="si-config__group" data-group="nvme">
      <header class="si-config__group-head"><h3 class="si-config__group-title">${esc(t.nvme)}</h3></header>
      <p class="si-config__meta si-config__caddy-auto">${esc(t.nvme_note)}</p>
      <ul class="si-config__list" role="list">
        <li><label class="si-config__item si-config__item--radio" data-kit="nvme">
          <input class="si-config__radio" type="checkbox" data-kit="nvme">
          <span class="si-config__thumb">${lead.img ? `<img src="${esc(lead.img)}" alt="" loading="lazy" width="56" height="56">` : ''}</span>
          <span class="si-config__text">
            <span class="si-config__title">${esc(t.nvme_kit)}</span>
            <span class="si-config__stock">${esc(names)}</span>
          </span>
          <span class="si-config__price">${esc(this.money(price))}</span>
        </label></li>
      </ul>
    </section>`;
  }

  /**
   * @param {string} title
   * @param {any[]} parts
   */
  #railsGroup(title, parts) {
    const t = this.data.t;
    const name = `si-config-rails-${this.data.server.v}`;
    const rows = parts
      .map(
        (part) => `<li><label class="si-config__item si-config__item--radio" data-v="${part.v}">
          <input class="si-config__radio" type="radio" name="${name}" value="${part.v}">
          <span class="si-config__thumb">${part.img ? `<img src="${esc(part.img)}" alt="" loading="lazy" width="56" height="56">` : ''}</span>
          <span class="si-config__text">
            <span class="si-config__title">${esc(part.t)}</span>
            <span class="si-config__stock si-config__stock--in">${esc(this.stockText(part))}</span>
          </span>
          <span class="si-config__price">${esc(this.money(part.price))}</span>
        </label></li>`
      )
      .join('');
    return `<section class="si-config__group" data-group="rails">
      <header class="si-config__group-head"><h3 class="si-config__group-title">${esc(title)}</h3></header>
      <ul class="si-config__list" role="list">
        <li><label class="si-config__item si-config__item--radio si-config__item--none">
          <input class="si-config__radio" type="radio" name="${name}" value="" checked>
          <span class="si-config__text"><span class="si-config__title">${esc(t.rails_none)}</span></span>
        </label></li>
        ${rows}
      </ul>
    </section>`;
  }

  /** @param {any} part */
  stockText(part) {
    const t = this.data.t;
    return part.stock >= 0 ? fill(t.in_stock, { count: part.stock }) : t.in_stock_plain;
  }

  /** @param {number} v */
  part(v) {
    return this.parts.find((p) => p.v === v);
  }

  /** @param {string} g */
  #sum(g, pred = (/** @type {any} */ _p) => true) {
    let n = 0;
    for (const [v, q] of this.qty) {
      const p = this.part(v);
      if (p && p.g === g && pred(p)) n += q;
    }
    return n;
  }

  /** Memory kind already chosen or fitted, if any. */
  #ramKind() {
    for (const [v, q] of this.qty) {
      const p = this.part(v);
      if (q > 0 && p?.g === 'ram' && p.kind) return p.kind;
    }
    return '';
  }

  /**
   * Highest quantity allowed for a part right now.
   * @param {any} p
   */
  max(p) {
    const have = this.qty.get(p.v) || 0;
    const { bays, slots, installedCount, installedKind } = this.hw;
    if (p.g === 'ram') {
      const kind = this.#ramKind();
      if (installedKind && p.kind && p.kind !== installedKind) return 0;
      if (kind && p.kind && p.kind !== kind) return 0;
      const free = Math.max(0, slots - installedCount - this.#sum('ram'));
      return Math.min(p.cap, have + free);
    }
    if (p.g === 'drive') {
      // NVMe bays are some of the SFF bays ("4NVME 24SFF"), so a U.2 drive uses both counts.
      const sffUsed = this.#sum('drive', (d) => d.ff === 'SFF' || d.io === 'NVME');
      if (p.io === 'NVME') {
        const nvmeFree = Math.max(0, bays.NVME - this.#sum('drive', (d) => d.io === 'NVME'));
        const sffFree = bays.SFF ? Math.max(0, bays.SFF - sffUsed) : nvmeFree;
        return Math.min(p.cap, have + Math.min(nvmeFree, sffFree));
      }
      const used = p.ff === 'SFF' ? sffUsed : this.#sum('drive', (d) => d.ff === p.ff && d.io !== 'NVME');
      return Math.min(p.cap, have + Math.max(0, bays[p.ff] - used));
    }
    if (p.g === 'caddy') {
      const free = Math.max(0, bays[p.ff] - this.#sum('caddy', (c) => c.ff === p.ff));
      return Math.min(p.cap, have + free);
    }
    if (p.g === 'rails') return Math.min(p.cap, 1);
    if (p.g === 'nvme') return this.nvmeOn ? 1 : 0;
    return 0;
  }

  /**
   * @param {any} p
   * @param {number} q
   */
  #set(p, q) {
    const value = Math.max(0, Math.min(q, this.max(p) || 0, p.cap));
    if (value > 0) this.qty.set(p.v, value);
    else this.qty.delete(p.v);
    return value;
  }

  /** Keeps the following caddy in step with a drive change of `delta`. */
  #followDrives(/** @type {any} */ drive, /** @type {number} */ delta) {
    const ff = drive.io === 'NVME' ? 'SFF' : drive.ff;
    const caddy = this.part(this.autoCaddy[ff]);
    if (!caddy || !delta) return;
    this.#set(caddy, (this.qty.get(caddy.v) || 0) + delta);
  }

  #onClick = (/** @type {MouseEvent} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const step = target.closest('[data-step]');
    if (step instanceof HTMLButtonElement) {
      const p = this.part(Number(step.dataset.v));
      if (!p) return;
      const before = this.qty.get(p.v) || 0;
      const after = this.#set(p, before + Number(step.dataset.step));
      if (p.g === 'drive') this.#followDrives(p, after - before);
      this.#update();
      return;
    }
    const fillButton = target.closest('[data-fill]');
    if (fillButton instanceof HTMLElement) {
      const ff = fillButton.dataset.fill || '';
      const caddy = this.part(this.autoCaddy[ff]);
      if (caddy) {
        // Room for the following caddy = bays minus the other caddies of the same size.
        const others = this.#sum('caddy', (c) => c.ff === ff && c.v !== caddy.v);
        this.#set(caddy, Math.max(0, this.hw.bays[ff] - others));
      }
      this.#update();
      return;
    }
    if (target.closest('[data-add]')) this.#addToCart();
    else if (target.closest('[data-quote]')) this.#toQuote();
  };

  #onChange = (/** @type {Event} */ event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.dataset.kit === 'nvme') {
      this.#setNvme(input.checked);
      this.#update();
      return;
    }
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    for (const p of this.parts) if (p.g === 'rails') this.qty.delete(p.v);
    const p = this.part(Number(input.value));
    if (p) this.#set(p, 1);
    this.#update();
  };

  /** Switches the NVMe kit; switching it off also takes out NVMe drives and their caddies. */
  #setNvme(/** @type {boolean} */ on) {
    this.nvmeOn = on && this.kitOffered;
    this.hw.bays.NVME = this.nvmeOn ? KIT_NVME_BAYS : this.baseNvme;
    for (const p of this.parts) {
      if (p.g === 'nvme') {
        if (this.nvmeOn) this.qty.set(p.v, 1);
        else this.qty.delete(p.v);
      }
    }
    if (!this.nvmeOn) {
      for (const p of this.parts) {
        if (p.g === 'drive' && p.io === 'NVME') {
          const had = this.qty.get(p.v) || 0;
          if (had) {
            this.qty.delete(p.v);
            this.#followDrives(p, -had);
          }
        }
      }
    }
  }

  #update() {
    const t = this.data.t;
    const kitRow = this.querySelector('.si-config__item[data-kit="nvme"]');
    if (kitRow) kitRow.classList.toggle('is-picked', this.nvmeOn);
    const driveMeta = this.querySelector('[data-meta="drive"]');
    if (driveMeta) {
      const { bays } = this.hw;
      driveMeta.textContent = [
        bays.SFF ? fill(t.bays_sff, { count: bays.SFF }) : '',
        bays.LFF ? fill(t.bays_lff, { count: bays.LFF }) : '',
        bays.NVME ? fill(t.bays_nvme, { count: bays.NVME }) : '',
      ].filter(Boolean).join(' · ');
    }
    for (const p of this.parts) {
      const q = this.qty.get(p.v) || 0;
      if (p.g === 'nvme') continue;
      const row = this.querySelector(`.si-config__item[data-v="${p.v}"]`);
      if (!row) continue;
      row.classList.toggle('is-picked', q > 0);
      if (p.g === 'rails') continue;
      const max = this.max(p);
      row.classList.toggle('is-blocked', q === 0 && max === 0);
      const out = row.querySelector('[data-count]');
      if (out) out.textContent = String(q);
      const minus = row.querySelector('[data-step="-1"]');
      const plus = row.querySelector('[data-step="1"]');
      if (minus instanceof HTMLButtonElement) minus.disabled = q <= 0;
      if (plus instanceof HTMLButtonElement) plus.disabled = q >= max;
    }

    // Memory notes: no memory fitted, a fitted kind that blocks the other, or a mix attempt.
    const note = this.querySelector('[data-note="ram"]');
    if (note) {
      const { installedKind, noRam } = this.hw;
      const blockedByInstalled = installedKind && this.parts.some((p) => p.g === 'ram' && p.kind && p.kind !== installedKind);
      const chosenKind = this.#ramKind();
      const blockedByChoice = chosenKind && this.parts.some((p) => p.g === 'ram' && p.kind && p.kind !== chosenKind);
      let text = '';
      if (blockedByInstalled) text = fill(t.ram_installed, { kind: installedKind });
      else if (blockedByChoice) text = t.ram_mix;
      else if (noRam && this.#sum('ram') === 0) text = t.no_ram;
      note.textContent = text;
      note.hidden = !text;
    }

    const { server } = this.data;
    const lines = [`<li class="si-config__line si-config__line--server"><span>${esc(server.title)}</span><span class="si-config__line-sum">${esc(this.money(server.price))}</span></li>`];
    let total = server.price;
    for (const p of this.parts) {
      const q = this.qty.get(p.v) || 0;
      if (!q) continue;
      total += q * p.price;
      lines.push(`<li class="si-config__line"><span>${q} × ${esc(p.t)}</span><span class="si-config__line-sum">${esc(this.money(q * p.price))}</span></li>`);
    }
    const list = this.querySelector('[data-lines]');
    if (list) list.innerHTML = lines.join('');
    const sum = this.querySelector('[data-total]');
    if (sum) sum.textContent = this.#sumText(total);
    this.total = total;
  }

  /** @param {string} text @param {boolean} [isError] */
  #status(text, isError = false) {
    const el = this.querySelector('[data-status]');
    if (!el) return;
    el.textContent = text;
    el.classList.toggle('si-config__status--error', isError);
  }

  #picked() {
    return this.parts.filter((p) => (this.qty.get(p.v) || 0) > 0).map((p) => ({ part: p, q: this.qty.get(p.v) || 0 }));
  }

  async #addToCart() {
    if (this.busy) return;
    const t = this.data.t;
    const button = this.querySelector('[data-add]');
    const { server } = this.data;
    const items = [{ id: server.v, quantity: 1 }];
    for (const { part, q } of this.#picked()) {
      items.push({ id: part.v, quantity: q, parent_id: server.v, properties: { [t.property]: server.short } });
    }

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    /** @type {any} */
    let events = null;
    try {
      events = await import('@shopify/events');
    } catch {
      events = null;
    }
    const Event = events?.CartLinesUpdateEvent;
    const deferred = Event?.createPromise?.();
    if (Event && deferred) {
      this.dispatchEvent(
        new Event({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
          promise: deferred.promise,
        })
      );
    }

    this.busy = true;
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.textContent = t.adding;
    }
    this.#status('');

    const routes = /** @type {any} */ (window).Theme?.routes || {};
    try {
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sectionIds.join(','), sections_url: window.location.pathname }),
      });
      const result = await response.json();
      const failed = !response.ok || result.status;
      const cart = await fetch(`${routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      if (Event && deferred) {
        deferred.resolve({
          cart: Event.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'si-server-config',
            sourceId: `si-server-config-${server.v}`,
            itemCount: items.reduce((n, i) => n + i.quantity, 0),
            productId: String(server.p),
            sections: failed ? undefined : result.sections,
            didError: Boolean(failed),
          },
        });
      }

      if (failed) {
        this.#status(result.description || result.message || t.error, true);
      } else if (!Event) {
        window.location.href = routes.cart_url || '/cart';
      } else {
        this.#status(t.added);
        // The drawer opens by itself only when the theme's auto-open setting is on.
        const status = this.querySelector('[data-status]');
        if (status && t.view_cart) {
          const link = document.createElement('a');
          link.href = routes.cart_url || '/cart';
          link.textContent = t.view_cart;
          status.append(' ', link);
        }
      }
    } catch (error) {
      console.error('[si-server-config]', error);
      deferred?.reject?.(error);
      this.#status(t.error, true);
    } finally {
      this.busy = false;
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
        button.textContent = t.add;
      }
    }
  }

  /** The build as plain text for the quote form. */
  buildText() {
    const { server, t } = this.data;
    const lines = [t.quote_intro, `${server.title}, ${this.money(server.price)}`];
    for (const { part, q } of this.#picked()) lines.push(`${q} × ${part.t}, ${this.money(q * part.price)}`);
    lines.push(`${t.total}: ${this.#sumText(this.total || server.price)}`);
    return lines.join('\n');
  }

  /** The total with "+ KM" behind it when the page shows net prices (quote-only servers). */
  #sumText(/** @type {number} */ cents) {
    const suffix = this.data.t.excl_vat ? ` ${this.data.t.excl_vat}` : '';
    return `${this.money(cents)}${suffix}`;
  }

  #toQuote() {
    const text = this.buildText();
    const form = document.querySelector('#quote-form form') || document.querySelector('#quote-form');
    const body = form?.querySelector('textarea[name="contact[body]"]');
    if (body instanceof HTMLTextAreaElement) {
      body.value = body.value.trim() ? `${body.value.trim()}\n\n${text}` : text;
      body.dispatchEvent(new Event('input', { bubbles: true }));
      const details = body.closest('details');
      if (details && !details.open) details.open = true;
      const name = form?.querySelector('input[name="contact[name]"]');
      (name instanceof HTMLInputElement && !name.value ? name : body).focus({ preventScroll: true });
      document.querySelector('#quote-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.#status(this.data.t.quote_done);
      return;
    }
    // No quote form on this page: carry the text to the business page.
    try {
      sessionStorage.setItem('si-config-request', text);
    } catch {
      /* storage blocked: the page still opens */
    }
    window.location.href = this.data.quoteUrl || '/';
  }
}

if (!customElements.get('si-server-config')) customElements.define('si-server-config', SiServerConfig);
